import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MedalIcon, FileTextIcon, MoreVerticalIcon, ArchiveIcon, TrashIcon, BookOpenIcon, CalculatorIcon, BeakerIcon, DnaIcon, PencilIcon, LandmarkIcon, GlobeIcon, PaletteIcon, MusicIcon, CodeIcon, ActivityIcon, UserIcon, SparklesIcon } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { StatusBadge as AssessmentStatusBadge } from './AssessmentStatusBadge';
import type { StatusType } from './AssessmentStatusBadge';
interface AssignedReportTableViewProps {
  reports: any[];
  archivedReports?: number[];
  onArchiveToggle?: (reportId: number) => void;
}
// Icon mapping function based on title
const getIconForReport = (title: string, mode: string) => {
  const lowerTitle = title.toLowerCase();
  if (lowerTitle.includes('math') || lowerTitle.includes('algebra') || lowerTitle.includes('fraction')) {
    return CalculatorIcon;
  }
  if (lowerTitle.includes('science') || lowerTitle.includes('chemistry') || lowerTitle.includes('matter')) {
    return BeakerIcon;
  }
  if (lowerTitle.includes('biology') || lowerTitle.includes('body') || lowerTitle.includes('dna')) {
    return DnaIcon;
  }
  if (lowerTitle.includes('english') || lowerTitle.includes('grammar') || lowerTitle.includes('writing')) {
    return PencilIcon;
  }
  if (lowerTitle.includes('history') || lowerTitle.includes('civilization')) {
    return LandmarkIcon;
  }
  if (lowerTitle.includes('geography') || lowerTitle.includes('continent')) {
    return GlobeIcon;
  }
  if (lowerTitle.includes('art') || lowerTitle.includes('painting')) {
    return PaletteIcon;
  }
  if (lowerTitle.includes('music') || lowerTitle.includes('theory')) {
    return MusicIcon;
  }
  if (lowerTitle.includes('computer') || lowerTitle.includes('programming')) {
    return CodeIcon;
  }
  if (lowerTitle.includes('physical') || lowerTitle.includes('sport')) {
    return ActivityIcon;
  }
  return BookOpenIcon;
};
const getIconColor = (title: string, mode: string): string => {
  const lowerTitle = title.toLowerCase();
  if (lowerTitle.includes('math') || lowerTitle.includes('algebra')) return '#2563EB';
  if (lowerTitle.includes('science') || lowerTitle.includes('chemistry')) return '#00B75F';
  if (lowerTitle.includes('biology') || lowerTitle.includes('dna')) return '#8B5CF6';
  if (lowerTitle.includes('english') || lowerTitle.includes('grammar')) return '#FF9D33';
  if (lowerTitle.includes('history')) return '#FF6CA1';
  if (lowerTitle.includes('geography')) return '#2563EB';
  if (lowerTitle.includes('art')) return '#FF6CA1';
  if (lowerTitle.includes('music')) return '#8B5CF6';
  if (lowerTitle.includes('computer') || lowerTitle.includes('programming')) return '#2563EB';
  if (lowerTitle.includes('physical') || lowerTitle.includes('sport')) return '#00B75F';
  // Fallback to mode-based colors
  if (mode === 'Challenge') return '#FF9D33';
  if (mode === 'Coach') return '#2563EB';
  return '#00B75F';
};
// Derive tags from mode and title
const getTagsFromReport = (title: string, mode: string): string[] => {
  const tags: string[] = [];
  const lowerTitle = title.toLowerCase();
  // Subject tag
  if (lowerTitle.includes('math')) tags.push('Math');else if (lowerTitle.includes('science')) tags.push('Science');else if (lowerTitle.includes('english')) tags.push('English');else if (lowerTitle.includes('history')) tags.push('History');else if (lowerTitle.includes('geography')) tags.push('Geography');else if (lowerTitle.includes('art')) tags.push('Art');else if (lowerTitle.includes('music')) tags.push('Music');else if (lowerTitle.includes('computer')) tags.push('Computer Science');else if (lowerTitle.includes('physical')) tags.push('PE');else if (lowerTitle.includes('biology')) tags.push('Biology');
  // Mode tag
  tags.push(mode);
  return tags;
};
const getTagColor = (index: number) => {
  const colors = ['bg-[#2563EB] bg-opacity-10 text-[#2563EB]', 'bg-[#00B75F] bg-opacity-10 text-[#00B75F]', 'bg-[#FF9D33] bg-opacity-10 text-[#FF9D33]', 'bg-[#FF6CA1] bg-opacity-10 text-[#FF6CA1]', 'bg-[#8B5CF6] bg-opacity-10 text-[#8B5CF6]'];
  return colors[index % colors.length];
};
// Helper to map student/archive status → draft | ready | archive
const getAssessmentStatusType = (status: string, isArchived: boolean): StatusType => {
  if (isArchived) return 'archive';
  if (status === 'Completed') return 'ready';
  return 'draft';
};
const AssignedReportTableView: React.FC<AssignedReportTableViewProps> = ({
  reports,
  archivedReports = [],
  onArchiveToggle
}) => {
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const menuRefs = useRef<{
    [key: number]: HTMLDivElement | null;
  }>({});
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuId !== null) {
        const menuRef = menuRefs.current[openMenuId];
        if (menuRef && !menuRef.contains(event.target as Node)) {
          setOpenMenuId(null);
        }
      }
    };
    if (openMenuId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuId]);
  const getActionButton = (status: string) => {
    switch (status) {
      case 'In progress':
        return {
          label: 'Resume',
          color: 'bg-[#FF9D33] hover:bg-[#ff8c1a]'
        };
      case 'Not started':
        return {
          label: 'Start',
          color: 'bg-[#00B75F] hover:bg-[#00a055]'
        };
      case 'Completed':
        return {
          label: 'View Report',
          color: 'bg-[#2563EB] hover:bg-[#1d4ed8]'
        };
      default:
        return {
          label: 'Start',
          color: 'bg-[#00B75F] hover:bg-[#00a055]'
        };
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };
  return <Tooltip.Provider delayDuration={200} data-id="element-594">
      <div className="bg-surface-elevated border border-custom rounded-2xl overflow-hidden shadow-custom-md" data-id="element-595">
        <div className="overflow-x-auto" data-id="element-596">
          <table className="w-full" data-id="element-597">
            <thead className="bg-surface border-b border-custom" data-id="element-598">
              <tr data-id="element-599">
                <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-600">
                  Report
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-601">
                  Creator
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-602">
                  Questions
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-603">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-604">
                  Last Activity
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-text-secondary uppercase tracking-wider" data-id="element-605">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-custom" data-id="element-606">
              {reports.map((report, index) => {
              const actionButton = getActionButton(report.status);
              const isCompleted = report.status === 'Completed';
              const isArchived = archivedReports.includes(report.id);
              const showLastActivity = report.status !== 'Not started' && report.lastInteractedWith;
              const IconComponent = getIconForReport(report.title, report.mode);
              const iconColor = getIconColor(report.title, report.mode);
              const tags = getTagsFromReport(report.title, report.mode);
              return <motion.tr key={report.id} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: index * 0.05
              }} className="hover:bg-surface transition-colors group" data-id="element-607">
                    <td className="px-6 py-4" data-id="element-608">
                      <div className="flex items-center gap-3" data-id="element-609">
                        {/* Icon with colored background and assignment badge */}
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform duration-200 relative" style={{
                      backgroundColor: `${iconColor}15`
                    }} data-id="element-610">
                          <IconComponent size={24} style={{
                        color: iconColor
                      }} data-id="element-611" />
                          {/* Assignment Type Badge on Icon */}
                          {report.assignedByTeacher !== undefined && <Tooltip.Root data-id="element-612">
                              <Tooltip.Trigger asChild data-id="element-613">
                                <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-surface-elevated rounded-full flex items-center justify-center shadow-sm border border-custom" data-id="element-614">
                                  {report.assignedByTeacher ? <UserIcon size={10} className="text-[#2563EB]" data-id="element-615" /> : <SparklesIcon size={10} className="text-[#FF9D33]" data-id="element-616" />}
                                </div>
                              </Tooltip.Trigger>
                              <Tooltip.Portal data-id="element-617">
                                <Tooltip.Content className="bg-surface-elevated border border-custom px-3 py-1.5 rounded-lg shadow-custom-lg text-xs font-medium text-text-primary z-50" sideOffset={5} data-id="element-618">
                                  {report.assignedByTeacher ? 'Assigned' : 'Self-initiated'}
                                  <Tooltip.Arrow className="fill-surface-elevated" data-id="element-619" />
                                </Tooltip.Content>
                              </Tooltip.Portal>
                            </Tooltip.Root>}
                        </div>
                        <div className="min-w-0" data-id="element-620">
                          {/* Title */}
                          <div className="font-semibold text-text-primary mb-1.5" data-id="element-621">
                            {report.title}
                          </div>
                          {/* Tags - matching card style */}
                          <div className="flex flex-wrap gap-1.5" data-id="element-622">
                            {tags.map((tag, idx) => <span key={idx} className={`text-xs font-semibold px-2 py-0.5 rounded-full ${getTagColor(idx)}`} data-id="element-623">
                                {tag}
                              </span>)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-text-primary font-medium" data-id="element-624">
                      {report.creator || 'System'}
                    </td>
                    <td className="px-6 py-4" data-id="element-625">
                      <div className="flex items-center gap-1" data-id="element-626">
                        <FileTextIcon size={14} className="text-text-tertiary" data-id="element-627" />
                        <span className="text-sm font-semibold text-text-primary" data-id="element-628">
                          {report.questions}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-629">
                      <AssessmentStatusBadge status={getAssessmentStatusType(report.status, isArchived)} size="sm" data-id="element-630" />
                    </td>
                    <td className="px-6 py-4" data-id="element-631">
                      {showLastActivity ? <span className="text-sm text-text-secondary" data-id="element-632">
                          {formatDate(report.lastInteractedWith)}
                        </span> : <span className="text-sm text-text-tertiary" data-id="element-633">—</span>}
                    </td>
                    <td className="px-6 py-4" data-id="element-634">
                      <div className="flex items-center justify-end gap-2" data-id="element-635">
                        {isCompleted && report.score !== undefined && <Tooltip.Root data-id="element-636">
                            <Tooltip.Trigger asChild data-id="element-637">
                              <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[#00B75F] bg-opacity-10 rounded-lg border border-[#00B75F] border-opacity-20 cursor-help" data-id="element-638">
                                <MedalIcon size={14} className="text-[#00B75F]" data-id="element-639" />
                                <span className="text-sm font-bold text-[#00B75F]" data-id="element-640">
                                  {report.score}%
                                </span>
                              </div>
                            </Tooltip.Trigger>
                            <Tooltip.Portal data-id="element-641">
                              <Tooltip.Content className="bg-surface-elevated border border-custom px-3 py-1.5 rounded-lg shadow-custom-lg text-xs font-medium text-text-primary z-50" sideOffset={5} data-id="element-642">
                                Your Score
                                <Tooltip.Arrow className="fill-surface-elevated" data-id="element-643" />
                              </Tooltip.Content>
                            </Tooltip.Portal>
                          </Tooltip.Root>}
                        <button className={`px-3 py-1.5 text-xs font-semibold text-white rounded-lg transition-colors ${actionButton.color}`} onClick={e => {
                      e.stopPropagation();
                      console.log('Action:', actionButton.label, report.id);
                    }} data-id="element-644">
                          {actionButton.label}
                        </button>
                        <div className="relative" ref={el => menuRefs.current[report.id] = el} data-id="element-645">
                          <button className="flex items-center justify-center w-8 h-8 rounded-lg bg-surface hover:bg-[#00B75F] hover:bg-opacity-10 text-text-secondary hover:text-[#00B75F] transition-all duration-200" aria-label="More actions" onClick={e => {
                        e.stopPropagation();
                        setOpenMenuId(openMenuId === report.id ? null : report.id);
                      }} data-id="element-646">
                            <MoreVerticalIcon size={14} data-id="element-647" />
                          </button>
                          <AnimatePresence data-id="element-648">
                            {openMenuId === report.id && <motion.div initial={{
                          opacity: 0,
                          scale: 0.95,
                          y: -10
                        }} animate={{
                          opacity: 1,
                          scale: 1,
                          y: 0
                        }} exit={{
                          opacity: 0,
                          scale: 0.95,
                          y: -10
                        }} transition={{
                          duration: 0.15
                        }} className="absolute right-0 mt-2 w-44 bg-surface-elevated rounded-xl shadow-custom-lg border border-custom overflow-hidden z-[100]" onClick={e => e.stopPropagation()} data-id="element-649">
                                <button className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-surface transition-colors text-left text-text-primary border-b border-custom" onClick={e => {
                            e.stopPropagation();
                            onArchiveToggle?.(report.id);
                            setOpenMenuId(null);
                          }} data-id="element-650">
                                  <ArchiveIcon size={16} data-id="element-651" />
                                  <span className="text-sm font-medium" data-id="element-652">
                                    {isArchived ? 'Unarchive' : 'Archive'}
                                  </span>
                                </button>
                                <button className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-surface transition-colors text-left text-red-600" onClick={e => {
                            e.stopPropagation();
                            console.log('Delete report:', report.id);
                            setOpenMenuId(null);
                          }} data-id="element-653">
                                  <TrashIcon size={16} data-id="element-654" />
                                  <span className="text-sm font-medium" data-id="element-655">
                                    Delete
                                  </span>
                                </button>
                              </motion.div>}
                          </AnimatePresence>
                        </div>
                      </div>
                    </td>
                  </motion.tr>;
            })}
            </tbody>
          </table>
        </div>
      </div>
    </Tooltip.Provider>;
};
export default AssignedReportTableView;